<template>
    <div class="modal-overlay">
      <div class="modal-container">
        <!-- Cabeçalho do Modal -->
        <div class="modal-header d-flex justify-content-between align-items-center">
          <h6 class="modal-title">{{ pessoa.id ? 'Editar Pessoa Física' : 'Nova Pessoa Física' }}</h6>
          <button class="btn btn-secondary btn-sm" @click="fecharModal">Fechar</button>
        </div>
  
        <!-- Formulário Base -->
        <BaseForm :isFormValid="isFormValid" :isSubmitting="isSubmitting" @submit="salvarPessoa" @cancel="fecharModal">
          <!-- Dados Pessoais -->
          <FormSection title="Dados Pessoais">
            <InputField 
              v-model="pessoa.nome" 
              label="Nome <span class='required'>*</span>" 
              id="nome" 
              placeholder="Digite o nome" 
              :class="{ 'error-input': errors.nome }"
              width="400px"
              required
            />
  
            <InputField 
              v-model="pessoa.data_nascimento" 
              label="Data de Nascimento <span class='required'>*</span>" 
              id="data_nascimento" 
              type="date" 
              :class="{ 'error-input': errors.data_nascimento }"
              width="150px"
              required
            />
  
            <InputField 
              v-model="pessoa.cpf" 
              label="CPF <span class='required'>*</span>" 
              id="cpf" 
              placeholder="Digite o CPF" 
              mask="cpf" 
              :class="{ 'error-input': errors.cpf }"
              width="150px"
              required
            />
            <span v-show="errors.cpf" class="error-message">{{ errors.cpf }}</span>
  
            <InputField 
              v-model="pessoa.rg" 
              label="RG" 
              id="rg" 
              placeholder="Digite o RG"
            />
            <SelectField 
              v-model="pessoa.sexo" 
              label="Sexo <span class='required'>*</span>" 
              id="sexo" 
              :options="[
                { value: 'M', text: 'Masculino' },
                { value: 'F', text: 'Feminino' }
              ]"
              :class="{ 'error-input': errors.sexo }"
              required
            />
            <InputField 
              v-model="pessoa.nacionalidade" 
              label="Nacionalidade" 
              id="nacionalidade" 
              placeholder="Digite a nacionalidade"
            />
            <InputField 
              v-model="pessoa.naturalidade" 
              label="Naturalidade" 
              id="naturalidade" 
              placeholder="Digite a naturalidade"
            />
            <!-- campo Sócio -->
            <SwitchField
              v-model="pessoa.socio"
              label="Sócio"
              id="socio"
            />
          </FormSection>
  
          <!-- Contato -->
          <FormSection title="Contato">
            <template #headerActions>
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm"
                title="Copiar contato e endereço do responsável"
                @click="modalResponsavel = true"
              >
                <i class="bi bi-files"></i>
              </button>
            </template>

            <datalist id="ddi-options-modal-pf">
              <option
                v-for="opt in ddiOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.text }}
              </option>
            </datalist>

            <div class="phone-row">
              <InputField
                v-model="pessoa.telefone_ddi"
                label="DDI"
                id="telefone_ddi_modal"
                placeholder="+55"
                list="ddi-options-modal-pf"
                maxlength="5"
                width="90px"
                :class="{ 'error-input': showTelefoneDdiError }"
                @blur="touched.telefone_ddi = true"
              />

              <InputField 
                v-model="pessoa.telefone" 
                label="Telefone principal" 
                id="telefone" 
                placeholder="Digite o telefone"
                :mask="telefoneMask"
                :maxlength="telefoneMaxlength"
                :class="{ 'error-input': showTelefoneError }"
                @blur="onTelefoneBlur"
              />
            </div>

            <div class="phone-row">
              <InputField
                v-model="pessoa.celular_ddi"
                label="DDI"
                id="celular_ddi_modal"
                placeholder="+55"
                list="ddi-options-modal-pf"
                maxlength="5"
                width="90px"
                :class="{ 'error-input': showCelularDdiError }"
                @blur="touched.celular_ddi = true"
              />

              <InputField
                v-model="pessoa.celular" 
                label="Celular / WhatsApp <span class='required'>*</span>" 
                id="celular" 
                placeholder="Digite o celular"
                :mask="celularMask"
                :maxlength="celularMaxlength"
                :class="{ 'error-input': showCelularError }"
                @blur="onCelularBlur"
              />
            </div>

            <InputField 
              v-model="pessoa.email" 
              label="Email <span class='required'>*</span>" 
              id="email" 
              placeholder="Digite o email"
            />
          </FormSection>
  
          <!-- Endereço -->
          <FormSection title="Endereço">
            <InputField 
              v-model="pessoa.cep" 
              label="CEP <span class='required'>*</span>"
              id="cep" 
              placeholder="Digite o CEP"
              mask="cep" 
            />
            <InputField 
              v-model="pessoa.endereco" 
              label="Endereço <span class='required'>*</span>" 
              id="endereco" 
              placeholder="Digite o endereço"
            />
            <InputField 
              v-model="pessoa.numero" 
              label="Número <span class='required'>*</span>" 
              id="numero"
            />
            <InputField 
              v-model="pessoa.bairro" 
              label="Bairro <span class='required'>*</span>" 
              id="bairro"
            />
            <InputField 
              v-model="pessoa.complemento" 
              label="Complemento" 
              id="complemento"
            />
            <SelectField 
              v-if="estados.length > 0" 
              v-model="pessoa.estado_cod" 
              label="Estado <span class='required'>*</span>" 
              id="estado" 
              :options="estados.map(e => ({ value: e.id, text: `${e.nome} (${e.sigla})` }))"
              @change="carregarMunicipios"
              :class="{ 'error-input': errors.estado_cod }"
            />
  
            <SelectField 
              v-if="municipios.length > 0" 
              v-model="pessoa.municipio_cod" 
              label="Município <span class='required'>*</span>" 
              id="municipio" 
              :options="municipios.map(m => ({ value: m.id, text: m.nome }))"
              :class="{ 'error-input': errors.municipio_cod }"
            />
          </FormSection>
  
          <!-- Observações -->
          <FormSection title="Observações">
            <textarea 
              v-model="pessoa.observacoes" 
              id="observacoes" 
              rows="3" 
              class="form-control"
              placeholder="Adicione observações"
            ></textarea>
          </FormSection>
        </BaseForm>
      </div>
    </div>

    <!-- Modal para escolher o responsável e clonar dados -->
    <CarregarPessoaFisica
      :mostrar="modalResponsavel"
      tipo-pessoa="responsavel"
      @fechar="modalResponsavel = false"
      @selecionar="onSelecionarResponsavel"
    />

  </template>
  
  <script>
  import { reactive, ref, computed, onMounted, watch } from 'vue';
  import axios from '@/services/axios';
  import InputField from '@/components/Layout/InputField.vue';
  import SelectField from '@/components/Layout/SelectField.vue';
  import FormSection from '@/components/Layout/FormSection.vue';
  import BaseForm from '@/components/Layout/BaseForm.vue';
  import globalUtils from '@/utils/globalUtils.js';
  import SwitchField from '@/components/Layout/SwitchField.vue';
  import CarregarPessoaFisica from '@/components/modals/CarregarPessoaFisica.vue'; // <= IMPORTAR

  export default {
    name: "ModalCadastrarPessoaFisica",
    components: { InputField, SelectField, FormSection, BaseForm, SwitchField, CarregarPessoaFisica },
    emits: ["fechar", "pessoaCadastrada"],
    setup(props, { emit }) {

      const pessoa = reactive({
        nome: '',
        cpf: '',
        rg: '',
        data_nascimento: '',
        sexo: '',
        telefone_ddi: '+55',
        telefone: '',
        celular_ddi: '+55',
        celular: '',
        email: '',
        endereco: '',
        numero: '',
        complemento: '',
        bairro: '',
        estado_cod: '',
        municipio_cod: '',
        cep: '',
        nacionalidade: '',
        naturalidade: '',
        socio: false,
        observacoes: '',
      });
  
      const estados = ref([]);
      const municipios = ref([]);
      const errors = reactive({});
      const isSubmitting = ref(false);
      const initialLoadComplete = ref(false);

      const formTouched = ref(false);

      const touched = reactive({
        telefone_ddi: false,
        telefone: false,
        celular_ddi: false,
        celular: false,
      });      

      const ddiOptions = [
        { value: '+55', text: 'Brasil +55' },
        { value: '+1', text: 'EUA/Canadá +1' },
        { value: '+351', text: 'Portugal +351' },
        { value: '+54', text: 'Argentina +54' },
        { value: '+598', text: 'Uruguai +598' },
        { value: '+595', text: 'Paraguai +595' },
        { value: '+56', text: 'Chile +56' },
        { value: '+39', text: 'Itália +39' },
        { value: '+34', text: 'Espanha +34' },
        { value: '+49', text: 'Alemanha +49' },
      ];
  
      // Normaliza string para comparação
      const normalizeString = (str) => {
        return str ? str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim() : '';
      };
  
      const modalResponsavel = ref(false);

      // Ao selecionar no modal, buscar o registro COMPLETO e copiar Contato + Endereço
      const onSelecionarResponsavel = async ({ pessoa: respMin }) => {
        try {
          // 1) Busca o registro completo por ID (garante todos os campos)
          const { data: resp } = await axios.get(`/cadastros/pessoas-fisicas/${respMin.id}`);

          // 2) Desliga watchers dependentes enquanto copiamos (evita auto-preenchimento via CEP)
          const prevFlag = initialLoadComplete.value;
          initialLoadComplete.value = false;

          // --- CONTATO
          pessoa.telefone_ddi = resp?.telefone_ddi ?? '+55';
          pessoa.telefone     = resp?.telefone ?? '';
          pessoa.celular_ddi  = resp?.celular_ddi ?? '+55';
          pessoa.celular      = resp?.celular  ?? '';
          pessoa.email        = resp?.email    ?? '';

          // --- ENDEREÇO
          pessoa.cep         = resp?.cep         ?? '';
          pessoa.endereco    = resp?.endereco    ?? '';
          pessoa.numero      = resp?.numero      ?? '';
          pessoa.complemento = resp?.complemento ?? '';
          pessoa.bairro      = resp?.bairro      ?? '';

          pessoa.estado_cod  = resp?.estado_cod  ?? '';
          if (pessoa.estado_cod) {
            await carregarMunicipios();
            pessoa.municipio_cod = resp?.municipio_cod ?? '';
          } else {
            pessoa.municipio_cod = '';
            municipios.value = [];
          }

          // 3) Reativa watchers
          initialLoadComplete.value = prevFlag;
          modalResponsavel.value = false;
        } catch (err) {
          console.error('Falha ao carregar responsável completo:', err);
          modalResponsavel.value = false;
        }
      };

      const limparTelefone = (valor) => (valor || '').replace(/\D/g, '');
      const normalizarDdi = (valor) => (valor || '').toString().trim();

      const validarDdi = (valor, obrigatorio = false) => {
        const ddi = normalizarDdi(valor);
        if (!ddi) return obrigatorio ? 'DDI é obrigatório.' : '';
        if (!/^\+\d{1,4}$/.test(ddi)) return 'DDI deve estar no formato +55.';
        return '';
      };

      const validarTelefonePorDdi = (valor, ddi, obrigatorio = false, label = 'Telefone') => {
        const digits = limparTelefone(valor);

        if (!digits) return obrigatorio ? `${label} é obrigatório.` : '';

        if (normalizarDdi(ddi) === '+55') {
          if (![10, 11].includes(digits.length)) {
            return `${label} deve ter 10 ou 11 dígitos.`;
          }
          return '';
        }

        if (digits.length < 6 || digits.length > 15) {
          return `${label} internacional deve ter entre 6 e 15 dígitos.`;
        }

        return '';
      };

      const validarTelefone = (valor) =>
        validarTelefonePorDdi(valor, pessoa.telefone_ddi, false, 'Telefone principal');

      const validarCelular = (valor) =>
        validarTelefonePorDdi(valor, pessoa.celular_ddi, true, 'Celular / WhatsApp');

      const onTelefoneBlur = () => {
        touched.telefone = true;
        touched.telefone_ddi = true;
        errors.telefone_ddi = validarDdi(pessoa.telefone_ddi, !!pessoa.telefone);
        errors.telefone = validarTelefone(pessoa.telefone);
      };

      const onCelularBlur = () => {
        touched.celular = true;
        touched.celular_ddi = true;
        errors.celular_ddi = validarDdi(pessoa.celular_ddi, true);
        errors.celular = validarCelular(pessoa.celular);
      };      

      watch(() => [pessoa.telefone, pessoa.telefone_ddi], () => {
        if (touched.telefone || touched.telefone_ddi || formTouched.value) {
          errors.telefone_ddi = validarDdi(pessoa.telefone_ddi, !!pessoa.telefone);
          errors.telefone = validarTelefone(pessoa.telefone);
        } else {
          errors.telefone_ddi = '';
          errors.telefone = '';
        }
      });

      watch(() => [pessoa.celular, pessoa.celular_ddi], () => {
        if (touched.celular || touched.celular_ddi || formTouched.value) {
          errors.celular_ddi = validarDdi(pessoa.celular_ddi, true);
          errors.celular = validarCelular(pessoa.celular);
        } else {
          errors.celular_ddi = '';
          errors.celular = '';
        }
      });

      // Validação do CPF conforme o usuário digita
      watch(() => pessoa.cpf, (newCpf) => {
        const cpfNumeros = newCpf.replace(/\D/g, '');
        if (cpfNumeros.length < 11) {
          errors.cpf = '';
        } else if (cpfNumeros.length === 11) {
          errors.cpf = !globalUtils.validarCPF(newCpf) ? 'CPF inválido.' : '';
        }
      });
  
      // Atualiza endereço via CEP (somente após o carregamento inicial)
      watch(() => pessoa.cep, async (newCep) => {
        if (!initialLoadComplete.value) return;
        const cepLimpo = newCep ? newCep.replace(/\D/g, '') : '';
        if (cepLimpo.length === 8) {
          try {
            const enderecoData = await globalUtils.buscarEnderecoPorCEP(newCep);
            pessoa.endereco = enderecoData.endereco || '';
            pessoa.bairro = enderecoData.bairro || '';
            pessoa.complemento = enderecoData.complemento || '';
  
            const estadoEncontrado = estados.value.find(e =>
              e.sigla.toUpperCase() === (enderecoData.estado || '').toUpperCase()
            );
            if (estadoEncontrado) {
              pessoa.estado_cod = estadoEncontrado.id;
              await carregarMunicipios();
              const municipioEncontrado = municipios.value.find(m =>
                normalizeString(m.nome) === normalizeString(enderecoData.municipio)
              );
              pessoa.municipio_cod = municipioEncontrado ? municipioEncontrado.id : '';
            }
          } catch (error) {
            console.error("Erro ao buscar endereço pelo CEP:", error.message);
          }
        }
      });
  
      // Computed para determinar se o formulário está válido
      const isFormValid = computed(() => {
        return (
          pessoa.nome.trim() !== '' &&
          pessoa.cpf.trim() !== '' &&
          globalUtils.validarCPF(pessoa.cpf) &&
          pessoa.data_nascimento.trim() !== '' &&
          pessoa.sexo.trim() !== '' &&
          pessoa.celular.trim() !== '' &&
          !validarDdi(pessoa.telefone_ddi, !!pessoa.telefone) &&
          !validarDdi(pessoa.celular_ddi, true) &&
          !validarTelefone(pessoa.telefone) &&
          !validarCelular(pessoa.celular) &&
          pessoa.email.trim() !== '' &&
          pessoa.endereco.trim() !== '' &&
          (pessoa.numero !== null && pessoa.numero.toString().trim() !== '') &&
          pessoa.bairro.trim() !== '' &&
          pessoa.cep.trim() !== '' &&
          (pessoa.municipio_cod !== null && pessoa.municipio_cod.toString().trim() !== '') &&
          (pessoa.estado_cod !== null && pessoa.estado_cod.toString().trim() !== '') &&
          !Object.values(errors).some(error => error)
        );
      });
        
      const telefoneMask = computed(() => normalizarDdi(pessoa.telefone_ddi) === '+55' ? 'telefone' : null);
      const celularMask = computed(() => normalizarDdi(pessoa.celular_ddi) === '+55' ? 'celular' : null);
      const telefoneMaxlength = computed(() => normalizarDdi(pessoa.telefone_ddi) === '+55' ? 15 : 25);
      const celularMaxlength = computed(() => normalizarDdi(pessoa.celular_ddi) === '+55' ? 15 : 25);

      const showTelefoneDdiError = computed(() => !!errors.telefone_ddi && (touched.telefone_ddi || formTouched.value));
      const showTelefoneError = computed(() => !!errors.telefone && (touched.telefone || formTouched.value));
      const showCelularDdiError = computed(() => !!errors.celular_ddi && (touched.celular_ddi || formTouched.value));
      const showCelularError = computed(() => !!errors.celular && (touched.celular || formTouched.value));

      const carregarEstados = async () => {
        try {
          const response = await axios.get(`/localidades/estados`);
          estados.value = response.data;
        } catch (error) {
          console.error('Erro ao carregar estados:', error);
        }
      };
  
      const carregarMunicipios = async () => {
        try {
          if (!pessoa.estado_cod) {
            municipios.value = [];
            return;
          }
          const response = await axios.get(`/localidades/municipios/${pessoa.estado_cod}`);
          municipios.value = response.data;
        } catch (error) {
          console.error('Erro ao carregar municípios:', error);
        }
      };
  
      const salvarPessoa = async () => {
        if (isSubmitting.value) return;

        formTouched.value = true;
        isSubmitting.value = true;

        touched.telefone = true;
        touched.celular = true;

        errors.telefone_ddi = validarDdi(pessoa.telefone_ddi, !!pessoa.telefone);
        errors.telefone = validarTelefone(pessoa.telefone);
        errors.celular_ddi = validarDdi(pessoa.celular_ddi, true);
        errors.celular = validarCelular(pessoa.celular);

        if (!isFormValid.value) {
          const mensagens = Object.values(errors).filter(Boolean);

          alert(
            mensagens.length
              ? `Não foi possível salvar:\n- ${mensagens.join('\n- ')}`
              : 'Não foi possível salvar. Verifique os campos obrigatórios.'
          );

          isSubmitting.value = false;
          return;
        }

        try {
          const url = pessoa.id
            ? `/cadastros/pessoas-fisicas/${pessoa.id}`
            : `/cadastros/pessoas-fisicas`;
          const method = pessoa.id ? 'put' : 'post';
          
          const response = await axios[method](url, pessoa);
          const novaPessoa = response.data;

          emit("pessoaCadastrada", novaPessoa);
          fecharModal();
        } catch (error) {
          console.error('Erro ao salvar pessoa física:', error.response?.data || error.message);
          alert(`Erro ao salvar pessoa: ${
            error.response?.data?.error ||
            error.response?.data?.message ||
            error.message ||
            'Erro desconhecido'
          }`);
        } finally {
          isSubmitting.value = false;
        }
      };
  
      const fecharModal = () => {
        emit("fechar");
      };
  
      onMounted(async () => {
        await carregarEstados();
        // Após carregar os estados, ativa a flag para CEP
        initialLoadComplete.value = true;
      });
  
      return {
        pessoa,
        estados,
        municipios,
        errors,
        isFormValid,
        isSubmitting,
        carregarMunicipios,
        salvarPessoa,
        fecharModal,
        normalizeString,
        modalResponsavel,
        onSelecionarResponsavel,

        formTouched,
        touched,
        ddiOptions,
        telefoneMask,
        celularMask,
        telefoneMaxlength,
        celularMaxlength,
        showTelefoneDdiError,
        showTelefoneError,
        showCelularDdiError,
        showCelularError,
        onTelefoneBlur,
        onCelularBlur,

      };
    },
  };
  </script>
  
  <style scoped>
  .modal-header {
    background: #ccc; /* cor cinza */
    padding: 10px 15px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }


  /* Estilos gerais do modal */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .modal-container {
  background: #fff;
  width: 100%;
  max-width: 60%; /* ajuste conforme necessário */
  height: 90%; /* define a altura fixa */
  overflow-y: auto; /* adiciona barra de rolagem vertical se necessário */
  margin: 0 10px;
  border-radius: 4px;
  /* Remova margens duplicadas se estiverem interferindo */
}

    /* Conteúdo do modal */
    .modal-content {
    padding: 15px;
  }


.modal {
  display: block;
  background: white;
  width: 60%;
  max-width: 90%;
  max-height: 80%;
  overflow-y: auto;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

  /* Ícone de fechar */
  .close-icon {
    font-size: 1.5rem;
    cursor: pointer;
  }


/* Barra de pesquisa */
.modal-filters {
  display: flex;
  align-items: center;
  gap: 10px;
}

.filter-label {
  font-size: 12px;
  font-weight: bold;
  color: #333;
  margin-top: 10px;
  margin-left: 10px;
}

.filter-group {
  display: flex;
  flex-grow: 1;
  gap: 10px;
  margin-right: 40px;
}

.filter-input {
  flex: 1;
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 0px;
  font-size: 10px;
  outline: none;
  transition: 0.2s;
  margin-left: 8px;
  margin-right: 15px;
}

.filter-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(0, 123, 255, 0.3);
}

/* Tabela */
.table-container {
  margin-top: 15px;
  margin-left: 15px;
  margin-right: 15px;
}

/* Ajusta a altura das células e centraliza verticalmente os conteúdos */
.table th, .table td {
  padding: 8px 10px; /* Reduz um pouco o espaçamento */
  line-height: 1.2; /* Ajusta a altura da linha */
  vertical-align: middle; /* Centraliza verticalmente */
}

.table td .btn-sm {
  padding: 4px 6px; /* Reduz o padding */
  font-size: 0.85rem; /* Diminui um pouco o tamanho do texto */
  height: 15px; /* Ajusta a altura */
  width: 15px; /* Mantém o botão quadrado */
  display: flex;
  align-items: center;
  justify-content: center;
}

.table th {
  background: #f1f1f1;
  font-weight: bold;
}

.table td {
  padding: 8px;
  border-bottom: 1px solid #ddd;
}

/* Rodapé */
/* 🔹 Ajusta a paginação e centraliza no Modal */
.modal-footer {
  display: flex;
  justify-content: center; /* Centraliza a paginação */
  align-items: center;
  margin-top: 10px;
  padding-top: 10px;
}

/* 🔹 Ajusta o layout da paginação */
.pagination-container {
  display: flex;
  align-items: center;
  gap: 10px; /* Espaço entre os elementos */
}

/* 🔹 Estiliza os botões de navegação da paginação */
.pagination-btn {
  padding: 6px 10px;
  font-size: 14px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 🔹 Estiliza o texto de página */
.pagination-text {
  font-size: 12px;
  font-weight: normal;
  color: #333;
}

/* 🔹 Ajusta a área de registros */
.total-registros {
  position: absolute;
  left: 20px;
  font-size: 14px;
  color: #555;
}

/* Ajusta a barra de filtros */
.modal-filters {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 15px;
}

/* Ajusta o grupo de filtros */
.filter-group {
  display: flex;
  flex-grow: 1;
  gap: 10px;
  align-items: center;
}

/* Estiliza o botão de limpar filtros */
.filter-btn {
  background: #6c757d;
  border: none;
  color: white;
  padding: 6px 12px;
  font-size: 16px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.filter-btn i {
  font-size: 18px;
}

.filter-btn:hover {
  background: #1a1b1b;
}

.small-filter-btn {
  padding: 7px 10px;      /* Reduz o espaçamento interno */
  font-size: 12px;       /* Fonte menor para o botão */
  line-height: 1;
}

.small-filter-btn i {
  font-size: 10px;       /* Diminui o tamanho do ícone, se desejar */
}

  .phone-row {
    display: grid;
    grid-template-columns: 90px minmax(180px, 1fr);
    gap: 0.5rem;
    align-items: end;
    max-width: 420px;
  }
  </style>
  